import { useState } from "react";
import useAxiosPrivate from "hooks/useAxiosPrivate";
import { PremiumModal, Button } from "components/ui";

const LEAVE_TYPES = [
  { value: "PAID",      label: "Paid leave"  },
  { value: "SICK",      label: "Sick leave"  },
  { value: "UNPAID",    label: "Unpaid"      },
  { value: "MATERNITY", label: "Maternity"   },
  { value: "OTHER",     label: "Other"       },
];

const emptyForm = {
  leaveType: "PAID",
  startDate: "",
  endDate: "",
  motif: "",
};

const inputStyle = {
  width: "100%",
  boxSizing: "border-box",
  borderRadius: 8,
  background: "rgba(255,255,255,0.05)",
  border: "1px solid rgba(255,255,255,0.1)",
  color: "inherit",
  padding: "9px 12px",
  fontSize: 13,
  outline: "none",
};

const labelStyle = {
  fontSize: 12,
  fontWeight: 600,
  display: "block",
  marginBottom: 6,
  color: "var(--text-primary, #f1f5f9)",
};

/**
 * LeaveRequestForm
 *
 * Props:
 *   isOpen   {boolean}   — controls modal visibility
 *   onClose  {function}  — called when modal should close
 *   onSuccess{function}  — called after successful submission (use to refresh parent list)
 */
const LeaveRequestForm = ({ isOpen, onClose, onSuccess }) => {
  const axiosPrivate = useAxiosPrivate();
  const [form, setForm]           = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError]         = useState("");

  const dayCount = () => {
    if (!form.startDate || !form.endDate) return null;
    const diff = (new Date(form.endDate) - new Date(form.startDate)) / 86_400_000;
    if (diff < 0) return null;
    return diff + 1;
  };

  const days = dayCount();

  const handleClose = () => {
    setForm(emptyForm);
    setError("");
    onClose();
  };

  const handleSubmit = async () => {
    if (!form.startDate || !form.endDate) {
      setError("Start date and end date are required.");
      return;
    }
    if (new Date(form.endDate) < new Date(form.startDate)) {
      setError("End date cannot be before start date.");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      await axiosPrivate.post("/api/rh/leave-requests", form);
      setForm(emptyForm);
      onSuccess?.();
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to submit request. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PremiumModal
      isOpen={isOpen}
      toggle={handleClose}
      title="New leave request"
      size="md"
      footer={
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <Button variant="secondary" onClick={handleClose} disabled={submitting}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSubmit} disabled={submitting}>
            {submitting ? "Submitting…" : "Submit request"}
          </Button>
        </div>
      }
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

        {/* Leave type */}
        <div>
          <label style={labelStyle}>Leave type</label>
          <select
            value={form.leaveType}
            onChange={(e) => setForm((f) => ({ ...f, leaveType: e.target.value }))}
            style={{ ...inputStyle, cursor: "pointer" }}
          >
            {LEAVE_TYPES.map((t) => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
        </div>

        {/* Date range */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <div>
            <label style={labelStyle}>Start date</label>
            <input
              type="date"
              value={form.startDate}
              min={new Date().toISOString().slice(0, 10)}
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  startDate: e.target.value,
                  // reset endDate if it's now before the new startDate
                  endDate: f.endDate && f.endDate < e.target.value ? "" : f.endDate,
                }))
              }
              style={inputStyle}
            />
          </div>
          <div>
            <label style={labelStyle}>End date</label>
            <input
              type="date"
              value={form.endDate}
              min={form.startDate || new Date().toISOString().slice(0, 10)}
              onChange={(e) => setForm((f) => ({ ...f, endDate: e.target.value }))}
              style={inputStyle}
            />
          </div>
        </div>

        {/* Duration preview */}
        {days !== null && (
          <div style={{
            background: "rgba(99,102,241,0.1)",
            border: "1px solid rgba(99,102,241,0.25)",
            borderRadius: 8, padding: "8px 14px",
            display: "flex", alignItems: "center", gap: 8,
          }}>
            <i className="ni ni-calendar-grid-58" style={{ color: "#818cf8", fontSize: 14 }} />
            <span style={{ fontSize: 12, color: "#818cf8", fontWeight: 500 }}>
              {days} {days === 1 ? "day" : "days"} total
            </span>
          </div>
        )}

        {/* Reason */}
        <div>
          <label style={labelStyle}>
            Reason{" "}
            <span style={{ color: "var(--text-muted, #94a3b8)", fontWeight: 400 }}>
              (optional)
            </span>
          </label>
          <textarea
            value={form.motif}
            onChange={(e) => setForm((f) => ({ ...f, motif: e.target.value }))}
            rows={3}
            placeholder="Briefly describe the reason for your absence…"
            style={{ ...inputStyle, resize: "vertical" }}
          />
        </div>

        {/* Error */}
        {error && (
          <div style={{
            background: "rgba(239,68,68,0.1)",
            border: "1px solid rgba(239,68,68,0.2)",
            borderRadius: 8, padding: "10px 14px",
            fontSize: 12, color: "#f87171",
            display: "flex", alignItems: "center", gap: 8,
          }}>
            <i className="ni ni-notification-70" style={{ fontSize: 13 }} />
            {error}
          </div>
        )}

      </div>
    </PremiumModal>
  );
};

export default LeaveRequestForm;